import { Stack, SxProps, Theme, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useState } from 'react';
import { ProjectHealthDto } from 'tdc-web-backend/projects/schemas';
import { useTranslation } from 'react-i18next';
import Health from '../../../../assets/icons/health.svg';
import PointerChip, { DirectionEnum } from '../../../../components/PointerChip/PointerChip';
import CustomNoRowsOverlay from '../../../../components/custom-no-rows-overlay/CustomNoRowsOverlay';

export interface ProjectHealthCardProps {
  health?: ProjectHealthDto;
  sx?: SxProps<Theme> | undefined;
}

export interface ProjectHealthPropertyProps {
  name: string;
  value: string;
  text: string;
  direction: DirectionEnum;
  className?: string;
}

const ProjectHealthProperty = ({
  direction,
  name,
  value,
  text,
  className,
}: ProjectHealthPropertyProps) => (
  <>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      className={className || undefined}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <PointerChip
          color={direction === DirectionEnum.Up ? '#0FA97F' : 'red'}
          direction={direction}
        />
        <Typography variant="body2" fontWeight={500} color="primaryDark.600">
          {name}
        </Typography>
      </Stack>

      <Stack spacing={0.5} direction="row">
        <Typography variant="body2" color="primaryDark.600">
          {text}
        </Typography>

        <Typography variant="body2" fontWeight={500} color="primaryDark.600">
          {value}
        </Typography>
      </Stack>
    </Stack>
  </>
);

export function ProjectHealthCard({ health, sx }: ProjectHealthCardProps) {
  const { t } = useTranslation();
  const sheduleCalc = Math.abs((health?.schedule as number) * 100).toFixed(1);
  const costCalc = Math.abs((health?.cost as number) * 100).toFixed(1);
  const scopeCalc = Math.abs((health?.cost as number) * 100).toFixed(1);
  const qualityCalc = Math.abs(health?.quality as number).toString();

  return (
    <Stack spacing={2} sx={{ position: 'relative' }}>
      {health == null && <CustomNoRowsOverlay />}
      <ProjectHealthProperty
        className="react-joyride-step-9-project"
        name={t('projects.healthPoints.schedule')}
        value={`${sheduleCalc}%`}
        text={
          (health?.schedule as number) >= 1
            ? t('projects.healthPoints.aheadSchedule')
            : t('projects.healthPoints.behindSchedule')
        }
        direction={(health?.schedule as number) >= 1 ? DirectionEnum.Up : DirectionEnum.Down}
      />
      <ProjectHealthProperty
        className="react-joyride-step-10-project"
        name={t('projects.healthPoints.cost')}
        value={`${costCalc}%`}
        text={
          (health?.cost as number) < 0
            ? t('projects.healthPoints.underBudget')
            : t('projects.healthPoints.overBudget')
        }
        direction={(health?.cost as number) <= 0 ? DirectionEnum.Up : DirectionEnum.Down}
      />
      <ProjectHealthProperty
        className="react-joyride-step-11-project"
        name={t('projects.healthPoints.scope')}
        value={scopeCalc}
        text={t('projects.healthPoints.changes')}
        direction={(health?.scope as number) <= 0 ? DirectionEnum.Up : DirectionEnum.Down}
      />
      <ProjectHealthProperty
        className="react-joyride-step-12-project"
        name={t('projects.healthPoints.quality')}
        value={qualityCalc}
        text={t('projects.healthPoints.projectIssues')}
        direction={(health?.quality as number) <= 0 ? DirectionEnum.Up : DirectionEnum.Down}
      />
    </Stack>
  );
}
